import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card, Badge, Tag, Button, Alert, message, Drawer, Select, InputNumber, List, Typography, Pagination } from 'antd';
import {
    ScanOutlined,
    PrinterOutlined,
    WifiOutlined,
    SettingOutlined,
    FileTextOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    LoadingOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useScannerInput } from './hooks/useScannerInput';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import {
    CHECK_ORDER_FOR_RELABEL,
    CheckOrderForRelabelResponse,
} from './graphql/queries/checkOrderForRelabel';
import {
    GET_TEMU_LABEL_FOR_RELABEL,
    GetTemuLabelForRelabelResponse,
    GetTemuLabelForRelabelVariables,
} from './graphql/queries/getTemuLabelForRelabel';
import { GET_TEMU_LABEL_FOR_RELABEL_FOR_EXTERNAL_STAFF } from './graphql/queries/getTemuLabelForRelabelForExternalStaff';
import { CHECK_ORDER_FOR_RELABEL_FOR_EXTERNAL_STAFF } from './graphql/queries/checkOrderForRelabelForExternalStaff';
import {
    GENERATE_SHIPPING_LABEL_PDF,
    GenerateShippingLabelPdfResponse,
    GenerateShippingLabelPdfVariables,
} from '../../delivery/parcels/graphql/mutations/generateShippingLabelPdf';
import {
    RECORD_LABEL_REPLACEMENT_PRINTING,
    RecordLabelReplacementPrintingResponse,
    RecordLabelReplacementPrintingVariables,
} from './graphql/mutations/recordLabelReplacementPrinting';
import { RECORD_LABEL_REPLACEMENT_PRINTING_FOR_EXTERNAL_STAFF } from './graphql/mutations/recordLabelReplacementPrintingForExternalStaff';
import {
    SAVE_ORDER_REPRINT_LOG,
    SaveOrderReprintLogResponse,
    SaveOrderReprintLogVariables,
} from './graphql/mutations/saveOrderReprintLog';
import { SAVE_ORDER_REPRINT_LOG_FOR_EXTERNAL_STAFF } from './graphql/mutations/saveOrderReprintLogForExternalStaff';
import {
    UPDATE_ORDER_REPRINT_CONFIRM,
    UpdateOrderReprintConfirmResponse,
    UpdateOrderReprintConfirmVariables,
} from './graphql/mutations/updateOrderReprintConfirm';
import { UPDATE_ORDER_REPRINT_CONFIRM_FOR_EXTERNAL_STAFF } from './graphql/mutations/updateOrderReprintConfirmForExternalStaff';
import {
    GET_ORDER_REPRINT_LOGS,
    OrderReprintLogsResponse,
    OrderReprintLogItem,
} from './graphql/queries/getOrderReprintLogs';
import { GET_ORDER_REPRINT_LOGS_FOR_EXTERNAL_STAFF } from './graphql/queries/getOrderReprintLogsForExternalStaff';
import {
    GET_ORDER_REPRINT_DAILY_STATS,
    OrderReprintDailyStatsResponse,
} from './graphql/queries/getOrderReprintDailyStats';
import { GET_ORDER_REPRINT_DAILY_STATS_FOR_EXTERNAL_STAFF } from './graphql/queries/getOrderReprintDailyStatsForExternalStaff';
import { checkPrintService, printPdf, fetchPrinterList } from './utils/printService';
import { normalizeTrackingScan } from './utils/trackingNumberParser';
import { getUserInfo, isHubOperator, isSortingStaff } from '../../../utils/auth';

interface PrintRecord {
    trackingNumber: string;
    recipient?: string;
    status: 'success' | 'failed' | 'printing';
    time: string;
    errorMessage?: string;
}


type ProcessingStatus = 'idle' | 'generating' | 'printing' | 'success' | 'queryFailed' | 'printFailed' | 'notTemuUsps' | 'noRelabel' | 'waitingConfirm' | 'confirmed';

interface PendingConfirmation {
    externalTrackingNumber: string;
    orderId?: number;
    logId: number;
    neutralTrackingNumber: string;
}

const OrderReprintPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
    const [statusMessage, setStatusMessage] = useState(t('wms.orderReprint.status.waitingScan'));
    const [latestRecord, setLatestRecord] = useState<PrintRecord | null>(null);
    const [printRecords, setPrintRecords] = useState<PrintRecord[]>([]);
    const [dailyStats, setDailyStats] = useState({ total: 0, success: 0, failed: 0 });
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [scannerActive, setScannerActive] = useState(false);
    const [startTime] = useState<Date>(new Date());
    const [runningTime, setRunningTime] = useState('0:00');
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [logVisible, setLogVisible] = useState(false);
    const [logPage, setLogPage] = useState(1);
    // 日志/统计来源筛选：1=人工(web)，2=小件快手；必填，默认人工
    const [logSource, setLogSource] = useState<number>(1);
    const LOG_PAGE_SIZE = 10;

    // 换单确认状态：打印成功后存储外部运单号，等待第二次扫描确认
    const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
    const [confirmedTrackingNumber, setConfirmedTrackingNumber] = useState<string>('');
    const [lastScannedCode, setLastScannedCode] = useState<string>('');

    // Print service status
    const [serviceReady, setServiceReady] = useState(false);
    const [serviceChecking, setServiceChecking] = useState(true);
    const [needsCertSetup, setNeedsCertSetup] = useState(false);
    const isMac = /macintosh|mac os x/i.test(navigator.userAgent);
    const [printerList, setPrinterList] = useState<string[]>([]);
    const [defaultPrinterName, setDefaultPrinterName] = useState<string>('');

    const PRINTER_STORAGE_KEY = 'wms_order_reprint_printer';
    const [printerName, setPrinterName] = useState<string>(
        () => localStorage.getItem(PRINTER_STORAGE_KEY) || ''
    );
    const [copies, setCopies] = useState<number>(1);

    const lastScanTimeRef = useRef<number>(0);

    // GraphQL hooks
    // 外部员工（HUB_OPERATOR / SORTING_STAFF）走 ForExternalStaff 版接口；其它账号保持原 query 不变。
    // 同时声明两套 useLazyQuery（符合 React hook rules），运行时按 externalStaff 切换 active fetcher。
    // 新 query 用 GraphQL 别名让响应字段名与原版一致，下游读取代码零改动。
    const externalStaff = isHubOperator() || isSortingStaff();

    const [checkOrderInternal] = useLazyQuery<CheckOrderForRelabelResponse>(CHECK_ORDER_FOR_RELABEL, { fetchPolicy: 'network-only' });
    const [checkOrderExternal] = useLazyQuery<CheckOrderForRelabelResponse>(CHECK_ORDER_FOR_RELABEL_FOR_EXTERNAL_STAFF, { fetchPolicy: 'network-only' });
    const checkOrder = externalStaff ? checkOrderExternal : checkOrderInternal;

    const [getTemuLabelInternal] = useLazyQuery<GetTemuLabelForRelabelResponse, GetTemuLabelForRelabelVariables>(
        GET_TEMU_LABEL_FOR_RELABEL, { fetchPolicy: 'network-only' }
    );
    const [getTemuLabelExternal] = useLazyQuery<GetTemuLabelForRelabelResponse, GetTemuLabelForRelabelVariables>(
        GET_TEMU_LABEL_FOR_RELABEL_FOR_EXTERNAL_STAFF, { fetchPolicy: 'network-only' }
    );
    const getTemuLabel = externalStaff ? getTemuLabelExternal : getTemuLabelInternal;

    const [generatePdf] = useMutation<GenerateShippingLabelPdfResponse, GenerateShippingLabelPdfVariables>(
        GENERATE_SHIPPING_LABEL_PDF
    );
    const [recordLabelReplacementInternal] = useMutation<RecordLabelReplacementPrintingResponse, RecordLabelReplacementPrintingVariables>(RECORD_LABEL_REPLACEMENT_PRINTING);
    const [recordLabelReplacementExternal] = useMutation<RecordLabelReplacementPrintingResponse, RecordLabelReplacementPrintingVariables>(RECORD_LABEL_REPLACEMENT_PRINTING_FOR_EXTERNAL_STAFF);
    const recordLabelReplacement = externalStaff ? recordLabelReplacementExternal : recordLabelReplacementInternal;

    const [saveLogInternal] = useMutation<SaveOrderReprintLogResponse, SaveOrderReprintLogVariables>(SAVE_ORDER_REPRINT_LOG);
    const [saveLogExternal] = useMutation<SaveOrderReprintLogResponse, SaveOrderReprintLogVariables>(SAVE_ORDER_REPRINT_LOG_FOR_EXTERNAL_STAFF);
    const saveLog = externalStaff ? saveLogExternal : saveLogInternal;

    const [updateConfirmInternal] = useMutation<UpdateOrderReprintConfirmResponse, UpdateOrderReprintConfirmVariables>(UPDATE_ORDER_REPRINT_CONFIRM);
    const [updateConfirmExternal] = useMutation<UpdateOrderReprintConfirmResponse, UpdateOrderReprintConfirmVariables>(UPDATE_ORDER_REPRINT_CONFIRM_FOR_EXTERNAL_STAFF);
    const updateConfirm = externalStaff ? updateConfirmExternal : updateConfirmInternal;

    const [fetchLogsInternal, fetchLogsInternalResult] = useLazyQuery<OrderReprintLogsResponse>(GET_ORDER_REPRINT_LOGS, { fetchPolicy: 'network-only' });
    const [fetchLogsExternal, fetchLogsExternalResult] = useLazyQuery<OrderReprintLogsResponse>(GET_ORDER_REPRINT_LOGS_FOR_EXTERNAL_STAFF, { fetchPolicy: 'network-only' });
    const fetchLogs = externalStaff ? fetchLogsExternal : fetchLogsInternal;
    const { data: logsData, loading: logsLoading } = externalStaff ? fetchLogsExternalResult : fetchLogsInternalResult;

    const [fetchTodayStatsInternal, fetchTodayStatsInternalResult] = useLazyQuery<OrderReprintDailyStatsResponse>(GET_ORDER_REPRINT_DAILY_STATS, { fetchPolicy: 'network-only' });
    const [fetchTodayStatsExternal, fetchTodayStatsExternalResult] = useLazyQuery<OrderReprintDailyStatsResponse>(GET_ORDER_REPRINT_DAILY_STATS_FOR_EXTERNAL_STAFF, { fetchPolicy: 'network-only' });
    const fetchTodayStats = externalStaff ? fetchTodayStatsExternal : fetchTodayStatsInternal;
    const { data: todayStatsData } = externalStaff ? fetchTodayStatsExternalResult : fetchTodayStatsInternalResult;

    const [fetchDrawerStatsInternal, fetchDrawerStatsInternalResult] = useLazyQuery<OrderReprintDailyStatsResponse>(GET_ORDER_REPRINT_DAILY_STATS, { fetchPolicy: 'network-only' });
    const [fetchDrawerStatsExternal, fetchDrawerStatsExternalResult] = useLazyQuery<OrderReprintDailyStatsResponse>(GET_ORDER_REPRINT_DAILY_STATS_FOR_EXTERNAL_STAFF, { fetchPolicy: 'network-only' });
    const fetchDrawerStats = externalStaff ? fetchDrawerStatsExternal : fetchDrawerStatsInternal;
    const { data: drawerStatsData } = externalStaff ? fetchDrawerStatsExternalResult : fetchDrawerStatsInternalResult;

    const currentAccountId = useMemo(() => {
        const id = getUserInfo()?.account?.id;
        const parsed = id ? parseInt(id, 10) : NaN;
        return Number.isFinite(parsed) ? parsed : undefined;
    }, []);

    // 仅人工(web)换单按操作员账户过滤；分拣机为系统账户(-1)，不按账户过滤
    const accountIdForSource = useCallback(
        (src?: number) => (src === 1 ? currentAccountId : undefined),
        [currentAccountId]
    );

    // Hub操作: hubId=自身ID, warehouseId=0; Warehouse操作: hubId=parentSiteId, warehouseId=自身ID
    const hasParentSite = !!(currentWarehouse?.parentSiteId && Number(currentWarehouse.parentSiteId) > 0);

    const currentHubId = useMemo(() => {
        if (!currentWarehouse?.id) return undefined;
        return hasParentSite ? parseInt(String(currentWarehouse.parentSiteId)) : parseInt(currentWarehouse.id);
    }, [currentWarehouse?.id, currentWarehouse?.parentSiteId, hasParentSite]);

    const currentWarehouseId = useMemo(() => {
        if (!currentWarehouse?.id) return 0;
        return hasParentSite ? parseInt(currentWarehouse.id) : 0;
    }, [currentWarehouse?.id, hasParentSite]);

    const refreshTodayStats = useCallback(() => {
        if (!currentWarehouse?.id) return;
        const todayStart = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
        fetchTodayStats({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, startTime: todayStart, accountId: currentAccountId } });
    }, [currentWarehouse?.id, fetchTodayStats, currentHubId, currentWarehouseId, currentAccountId]);

    useEffect(() => {
        if (!currentWarehouse?.id) return;
        refreshTodayStats();
        const timer = setInterval(refreshTodayStats, 60000);
        return () => clearInterval(timer);
    }, [currentWarehouse?.id, refreshTodayStats]);

    // 服务器数据加载后同步本地计数（作为基准，之后本地 +1）
    useEffect(() => {
        const today = todayStatsData?.getOrderReprintDailyStats?.[0];
        if (!today) return;
        setDailyStats({ total: Number(today.total), success: Number(today.success), failed: Number(today.failed) });
    }, [todayStatsData]);

    // Check local print service on mount
    const detectService = useCallback(async (): Promise<boolean> => {
        setServiceChecking(true);
        setNeedsCertSetup(false);
        let ready = false;
        try {
            const status = await checkPrintService();
            ready = status.ready;
            setServiceReady(ready);
            setNeedsCertSetup(status.needsCertSetup);
            if (!ready) {
                setPrinterList([]);
                setPrinterName('');
                setDefaultPrinterName('');
            } else if (status.printers.length > 0) {
                setPrinterList(status.printers);
                setDefaultPrinterName(status.defaultPrinter);
                // 服务可用时，若当前无选中打印机则自动选中系统默认
                if (status.defaultPrinter) {
                    setPrinterName(prev => prev || status.defaultPrinter);
                }
            }
        } catch {
            setServiceReady(false);
            setPrinterList([]);
            setPrinterName('');
            setDefaultPrinterName('');
        }
        setServiceChecking(false);
        return ready;
    }, []);

    useEffect(() => {
        // 延迟1秒检测，等待 QZ Tray 就绪
        const timer = setTimeout(detectService, 1000);
        return () => clearTimeout(timer);
    }, [detectService]);

    // Update running time every second
    useEffect(() => {
        const timer = setInterval(() => {
            const diff = Date.now() - startTime.getTime();
            const hours = Math.floor(diff / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setRunningTime(`${hours}${t('wms.orderReprint.stats.hours')}${minutes.toString().padStart(2, '0')}${t('wms.orderReprint.stats.minutes')}${seconds.toString().padStart(2, '0')}${t('wms.orderReprint.stats.seconds')}`);
        }, 1000);
        return () => clearInterval(timer);
    }, [startTime, t]);

    // Network status detection
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const getErrorMessage = useCallback((error: any): string => {
        if (error?.networkError) {
            return t('wms.orderReprint.status.networkError') as string;
        }
        return (error?.graphQLErrors?.[0]?.message || error?.message || t('wms.orderReprint.status.unknownError')) as string;
    }, [t]);

    // Handle scan result — 按流程图实现完整分支逻辑
    const handleScan = useCallback(async (rawCode: string) => {
        // Debounce: prevent duplicate scans within 2 seconds
        const now = Date.now();
        if (now - lastScanTimeRef.current < 2000) return;
        lastScanTimeRef.current = now;

        // 归一化：USPS 420开头取后22位，FedEx 96开头取后14位去前导零
        const code = normalizeTrackingScan(rawCode);

        // Mark scanner as active on first successful scan
        if (!scannerActive) setScannerActive(true);

        const scanStartTime = Date.now();
        const currentTime = new Date().toLocaleTimeString(undefined, { hour12: false });

        // ── 分支1: 上一个打印面单是否已扫描确认 ──
        if (pendingConfirmation) {
            // 无论匹配与否，都记录实际扫描的单号
            updateConfirm({ variables: { id: pendingConfirmation.logId, reprintTrackingNumber: code } }).catch(() => {});

            // 未确认 → 检查是否是上次换单打印的换后运单号
            if (code === pendingConfirmation.externalTrackingNumber) {
                // 匹配 → 上一单换单确认
                setConfirmedTrackingNumber(pendingConfirmation.externalTrackingNumber);
                setPendingConfirmation(null);
                setProcessingStatus('confirmed');
                setStatusMessage(t('wms.orderReprint.status.relabelConfirmed'));
                message.success(t('wms.orderReprint.status.relabelConfirmed') as string);
                setTimeout(() => {
                    setProcessingStatus('idle');
                    setStatusMessage(t('wms.orderReprint.status.waitingScan'));
                }, 3000);
            } else {
                // 不匹配 → 提示请扫描所换面单确认换单完成
                setLastScannedCode(code);
                setProcessingStatus('waitingConfirm');
                setStatusMessage(t('wms.orderReprint.status.pleaseScanReplacedLabel'));
                message.warning(t('wms.orderReprint.status.pleaseScanReplacedLabel') as string);
            }
            return;
        }

        // ── 分支2: 已确认（或首次扫描），走新单流程 ──
        if (!serviceReady) {
            message.error(t('wms.orderReprint.printService.notRunning') as string);
            return;
        }

        setProcessingStatus('generating');
        setStatusMessage(t('wms.orderReprint.status.generating'));

        const newRecord: PrintRecord = {
            trackingNumber: code,
            status: 'printing',
            time: currentTime,
        };

        // Step 1: 查询订单并检查是否TEMU-USPS、是否需要换单
        let orderCheck: { orderId: number; trackingNumber?: string; externalTrackingNumber?: string; isTemuUsps: boolean; needsRelabel: boolean; recipientName?: string; recipientAddressLine?: string; recipientCityLine?: string } | null = null;
        try {
            const { data: checkData } = await checkOrder({
                variables: { neutralTrackingNumber: code },
            });
            orderCheck = checkData?.checkOrderForRelabel ?? null;
        } catch (error: any) {
            const errorMsg = getErrorMessage(error);
            newRecord.status = 'failed';
            newRecord.errorMessage = errorMsg as string;
            setProcessingStatus('queryFailed');
            setStatusMessage(t('wms.orderReprint.status.queryFailed') as string);
            message.error(`${t('wms.orderReprint.status.queryFailed')}: ${errorMsg}`);
            setDailyStats(prev => ({ total: prev.total + 1, success: prev.success, failed: prev.failed + 1 }));
            setLatestRecord(newRecord);
            setPrintRecords(prev => [newRecord, ...prev].slice(0, 500));
            saveLog({ variables: {
                hubId: currentHubId,
                warehouseId: currentWarehouseId,
                accountId: currentAccountId,
                neutralTrackingNumber: code,
                status: 2,
                failureType: 1,
                failureReason: errorMsg,
                processingTimeMs: Date.now() - scanStartTime,
            }}).catch(() => {});
            setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
            return;
        }

        // Step 1-TEMU: checkOrderForRelabel 查无此单时，尝试 TEMU 兜底
        if (orderCheck === null) {
            const { data: temuData } = await getTemuLabel({ variables: { collectionWaybillSn: code } }).catch(() => ({ data: null }));
            const temuLabel = temuData?.getTemuLabelForRelabel ?? null;

            if (!temuLabel) {
                // TEMU 也查无此单，走原有查无此单逻辑
                const errorMsg = t('wms.orderReprint.status.orderNotFound') as string;
                newRecord.status = 'failed';
                newRecord.errorMessage = errorMsg;
                setProcessingStatus('queryFailed');
                setStatusMessage(t('wms.orderReprint.status.queryFailed') as string);
                message.error(`${t('wms.orderReprint.status.queryFailed')}: ${errorMsg}`);
                setDailyStats(prev => ({ total: prev.total + 1, success: prev.success, failed: prev.failed + 1 }));
                setLatestRecord(newRecord);
                setPrintRecords(prev => [newRecord, ...prev].slice(0, 500));
                saveLog({ variables: {
                    hubId: currentHubId,
                    warehouseId: currentWarehouseId,
                    accountId: currentAccountId,
                    neutralTrackingNumber: code,
                    status: 2,
                    failureType: 1,
                    failureReason: errorMsg,
                    processingTimeMs: Date.now() - scanStartTime,
                }}).catch(() => {});
                setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
                return;
            }

            // TEMU 命中：跳过 Step 2-3，直接打印
            try {
                setProcessingStatus('printing');
                setStatusMessage(t('wms.orderReprint.status.printing'));

                await printPdf(temuLabel.pdfBase64, { printer: printerName || undefined, copies });

                newRecord.status = 'success';
                setDailyStats(prev => ({ total: prev.total + 1, success: prev.success + 1, failed: prev.failed }));

                const saveResult = await saveLog({ variables: {
                    hubId: currentHubId,
                    warehouseId: currentWarehouseId,
                    accountId: currentAccountId,
                    neutralTrackingNumber: code,
                    trackingNumber: temuLabel.tailWaybillSn || undefined,
                    status: 1,
                    processingTimeMs: Date.now() - scanStartTime,
                }}).catch(() => null);
                const logId = saveResult?.data?.saveOrderReprintLog ?? 0;

                if (temuLabel.tailWaybillSn) {
                    setPendingConfirmation({
                        externalTrackingNumber: temuLabel.tailWaybillSn,
                        logId,
                        neutralTrackingNumber: code,
                    });
                    setProcessingStatus('waitingConfirm');
                    setStatusMessage(t('wms.orderReprint.status.scanToConfirm'));
                } else {
                    setProcessingStatus('success');
                    setStatusMessage(t('wms.orderReprint.status.printSuccess'));
                    setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
                }
            } catch (error: any) {
                const errorMsg = getErrorMessage(error);
                newRecord.status = 'failed';
                newRecord.errorMessage = errorMsg;
                setProcessingStatus('printFailed');
                setStatusMessage(t('wms.orderReprint.status.printFailedRetry') as string);
                message.error(`${t('wms.orderReprint.status.printError')}: ${errorMsg}`);
                setDailyStats(prev => ({ total: prev.total + 1, success: prev.success, failed: prev.failed + 1 }));
                saveLog({ variables: {
                    hubId: currentHubId,
                    warehouseId: currentWarehouseId,
                    accountId: currentAccountId,
                    neutralTrackingNumber: code,
                    status: 2,
                    failureType: 2,
                    failureReason: errorMsg,
                    processingTimeMs: Date.now() - scanStartTime,
                }}).catch(() => {});
                setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
            }

            setLatestRecord(newRecord);
            setPrintRecords(prev => [newRecord, ...prev].slice(0, 500));
            return;
        }

        // Step 2 & 3: 校验订单类型与换单需求
        const validationCheck: { pass: boolean; status?: ProcessingStatus; msgKey?: string } =
            !orderCheck.isTemuUsps ? { pass: false, status: 'notTemuUsps', msgKey: 'wms.orderReprint.status.notTemuUsps' } :
            !orderCheck.needsRelabel ? { pass: false, status: 'noRelabel', msgKey: 'wms.orderReprint.status.noRelabelNeeded' } :
            { pass: true };

        if (!validationCheck.pass) {
            newRecord.status = 'failed';
            newRecord.errorMessage = t(validationCheck.msgKey!) as string;
            setProcessingStatus(validationCheck.status!);
            setStatusMessage(t(validationCheck.msgKey!));
            message.warning(t(validationCheck.msgKey!) as string);
            setLatestRecord(newRecord);
            setPrintRecords(prev => [newRecord, ...prev].slice(0, 500));
            setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
            return;
        }

        // Step 4: 请求面单PDF + 打印
        try {
            const orderId = orderCheck.orderId;
            const { data: pdfData } = await generatePdf({ variables: { orderId } });

            if (!pdfData?.generateShippingLabelPdf) {
                throw new Error(t('wms.orderReprint.status.pdfGenerateFailed') as string);
            }

            // Step 5: 打印面单
            setProcessingStatus('printing');
            setStatusMessage(t('wms.orderReprint.status.printing'));

            await printPdf(pdfData.generateShippingLabelPdf, {
                printer: printerName || undefined,
                copies,
            });

            // 打印成功
            newRecord.status = 'success';
            newRecord.recipient = [orderCheck.recipientName, orderCheck.recipientAddressLine, orderCheck.recipientCityLine].filter(Boolean).join('\n') || '--';

            // 记录轨迹: LabelReplacementPrinted
            recordLabelReplacement({
                variables: {
                    orderId,
                    warehouseId: currentWarehouseId,
                    warehouseName: currentWarehouse?.name || undefined,
                },
            }).catch(() => {});

            setDailyStats(prev => ({ total: prev.total + 1, success: prev.success + 1, failed: prev.failed }));
            const saveResult = await saveLog({ variables: {
                hubId: currentHubId,
                warehouseId: currentWarehouseId,
                accountId: currentAccountId,
                neutralTrackingNumber: code,
                trackingNumber: orderCheck.trackingNumber || undefined,
                status: 1,
                processingTimeMs: Date.now() - scanStartTime,
            }}).catch(() => null);
            const logId = saveResult?.data?.saveOrderReprintLog ?? 0;

            // 进入"待确认"状态 — 等待扫描换后运单号确认
            if (orderCheck.externalTrackingNumber) {
                setPendingConfirmation({
                    externalTrackingNumber: orderCheck.externalTrackingNumber,
                    orderId,
                    logId,
                    neutralTrackingNumber: code,
                });
                setProcessingStatus('waitingConfirm');
                setStatusMessage(t('wms.orderReprint.status.scanToConfirm'));
            } else {
                // 无外部运单号，跳过确认
                setProcessingStatus('success');
                setStatusMessage(t('wms.orderReprint.status.printSuccess'));
                setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
            }
        } catch (error: any) {
            const errorMsg = getErrorMessage(error);
            newRecord.status = 'failed';
            newRecord.errorMessage = errorMsg as string;
            setProcessingStatus('printFailed');
            setStatusMessage(t('wms.orderReprint.status.printFailedRetry') as string);
            message.error(`${t('wms.orderReprint.status.printError')}: ${errorMsg}`);
            setDailyStats(prev => ({ total: prev.total + 1, success: prev.success, failed: prev.failed + 1 }));
            saveLog({ variables: {
                hubId: currentHubId,
                warehouseId: currentWarehouseId,
                accountId: currentAccountId,
                neutralTrackingNumber: code,
                trackingNumber: orderCheck?.trackingNumber || undefined,
                status: 2,
                failureType: 2,
                failureReason: errorMsg,
                processingTimeMs: Date.now() - scanStartTime,
            }}).catch(() => {});
            setTimeout(() => { setProcessingStatus('idle'); setStatusMessage(t('wms.orderReprint.status.waitingScan')); }, 3000);
        }

        setLatestRecord(newRecord);
        setPrintRecords(prev => [newRecord, ...prev].slice(0, 500));
    }, [serviceReady, copies, printerName, t, checkOrder, getTemuLabel, generatePdf, currentWarehouse, getErrorMessage, recordLabelReplacement, saveLog, updateConfirm, currentAccountId, currentHubId, currentWarehouseId, pendingConfirmation]);

    // Initialize scanner input hook
    useScannerInput({
        onScan: handleScan,
        enabled: true,
    });

    const getStatusColor = (): string => {
        switch (processingStatus) {
            case 'generating':
            case 'printing': return '#1890ff';
            case 'success':
            case 'confirmed': return '#52c41a';
            case 'queryFailed':
            case 'notTemuUsps':
            case 'noRelabel': return '#fa8c16';
            case 'printFailed': return '#ff4d4f';
            case 'waitingConfirm': return '#722ed1';
            default: return '#52c41a';
        }
    };

    const getStatusBgColor = (): string => {
        switch (processingStatus) {
            case 'generating':
            case 'printing': return '#e6f7ff';
            case 'success':
            case 'confirmed': return '#f6ffed';
            case 'queryFailed':
            case 'notTemuUsps':
            case 'noRelabel': return '#fff7e6';
            case 'printFailed': return '#fff2f0';
            case 'waitingConfirm': return '#f9f0ff';
            default: return '#f6ffed';
        }
    };

    const getStatusIcon = () => {
        switch (processingStatus) {
            case 'generating':
            case 'printing':
                return <LoadingOutlined style={{ fontSize: 20, color: '#1890ff' }} />;
            case 'success':
            case 'confirmed':
                return <CheckCircleFilled style={{ fontSize: 20, color: '#52c41a' }} />;
            case 'queryFailed':
            case 'notTemuUsps':
            case 'noRelabel':
                return <ExclamationCircleFilled style={{ fontSize: 20, color: '#fa8c16' }} />;
            case 'printFailed':
                return <CloseCircleFilled style={{ fontSize: 20, color: '#ff4d4f' }} />;
            case 'waitingConfirm':
                return <ScanOutlined style={{ fontSize: 20, color: '#722ed1' }} />;
            default:
                return <ScanOutlined style={{ fontSize: 20, color: '#52c41a' }} />;
        }
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div
                style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '16px 24px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <span style={{ color: '#fff', fontSize: 20, fontWeight: 600 }}>
                    {t('wms.orderReprint.title')}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => {
                            setSettingsVisible(true);
                            // 延迟加载完整打印机列表
                            if (serviceReady && printerList.length <= 1) {
                                fetchPrinterList().then(list => {
                                    if (list.length > 0) setPrinterList(list);
                                });
                            }
                        }}
                        style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}
                    >
                        {t('wms.orderReprint.settings')}
                    </Button>
                    <Button
                        icon={<FileTextOutlined />}
                        onClick={() => {
                            setLogVisible(true);
                            setLogPage(1);
                            if (currentWarehouse?.id) {
                                fetchLogs({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, accountId: accountIdForSource(logSource), source: logSource, page: 1, size: LOG_PAGE_SIZE } });
                                fetchDrawerStats({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, accountId: accountIdForSource(logSource), source: logSource } });
                            }
                        }}
                        style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}
                    >
                        {t('wms.orderReprint.log')}
                    </Button>
                </div>
            </div>

            {/* Print service not running warning - Mac */}
            {!serviceChecking && !serviceReady && isMac && (
                <Alert
                    type="warning"
                    showIcon
                    banner
                    message={t('wms.orderReprint.printService.macNotRunningTip')}
                    action={
                        <Button
                            size="small"
                            onClick={detectService}
                            loading={serviceChecking}
                            style={{ color: '#d48806', borderColor: '#d48806' }}
                        >
                            {t('wms.orderReprint.printService.retry')}
                        </Button>
                    }
                />
            )}
            {/* Print service not running warning - Windows */}
            {!serviceChecking && !serviceReady && !isMac && (
                <Alert
                    type="error"
                    showIcon
                    banner
                    message={t('wms.orderReprint.printService.winNotRunningTip')}
                    description={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                            <span>{t('wms.orderReprint.printService.winNotRunningDesc')}</span>
                            <Button
                                size="small"
                                type="primary"
                                danger
                                icon={<DownloadOutlined />}
                                href="/print-service/win-print-service-setup.zip"
                                download="win-print-service-setup.zip"
                            >
                                {t('wms.orderReprint.printService.downloadSetup')}
                            </Button>
                            <Button
                                size="small"
                                onClick={detectService}
                                loading={serviceChecking}
                            >
                                {t('wms.orderReprint.printService.retry')}
                            </Button>
                        </div>
                    }
                />
            )}

            {/* Main Content */}
            <div style={{ flex: 1, padding: 24, display: 'flex', gap: 24, background: '#f5f5f5' }}>
                {/* Left Card - Device Status */}
                <Card
                    title={t('wms.orderReprint.deviceStatus.title')}
                    style={{ flex: '0 0 280px' }}
                    headStyle={{ textAlign: 'center', fontWeight: 600, fontSize: 16 }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '8px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Badge color={scannerActive ? 'green' : 'orange'} />
                            <ScanOutlined style={{ fontSize: 18 }} />
                            <span style={{ fontSize: 15 }}>
                                {t('wms.orderReprint.deviceStatus.scanner')}
                                {scannerActive
                                    ? t('wms.orderReprint.deviceStatus.connected')
                                    : t('wms.orderReprint.deviceStatus.waitingFirstScan')}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Badge color={serviceChecking ? 'orange' : serviceReady ? 'green' : 'red'} />
                            <PrinterOutlined style={{ fontSize: 18 }} />
                            <span style={{ fontSize: 15 }}>
                                {t('wms.orderReprint.deviceStatus.printer')}
                                {serviceChecking
                                    ? t('wms.orderReprint.printService.checking')
                                    : serviceReady
                                        ? t('wms.orderReprint.deviceStatus.ready')
                                        : t('wms.orderReprint.printService.notRunning')}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Badge color={isOnline ? 'green' : 'red'} />
                            <WifiOutlined style={{ fontSize: 18 }} />
                            <span style={{ fontSize: 15 }}>
                                {t('wms.orderReprint.deviceStatus.network')}
                                {isOnline
                                    ? t('wms.orderReprint.deviceStatus.normal')
                                    : t('wms.orderReprint.deviceStatus.disconnected')}
                            </span>
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 32,
                            textAlign: 'center',
                            color: serviceReady ? '#52c41a' : '#ff4d4f',
                            fontSize: 16,
                            fontWeight: 500,
                        }}
                    >
                        {serviceReady
                            ? t('wms.orderReprint.deviceStatus.hint')
                            : t('wms.orderReprint.printService.startFirst')}
                    </div>
                </Card>

                {/* Right Card - Processing Status */}
                <Card
                    title={t('wms.orderReprint.processStatus.title')}
                    style={{ flex: 1 }}
                    headStyle={{ textAlign: 'center', fontWeight: 600, fontSize: 16 }}
                >
                    {/* Current Status */}
                    <div
                        style={{
                            background: getStatusBgColor(),
                            border: `1px solid ${getStatusColor()}33`,
                            borderRadius: 8,
                            padding: '16px 20px',
                            textAlign: 'center',
                            marginBottom: 24,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
                            {getStatusIcon()}
                            <span style={{ fontSize: 16, fontWeight: 600, color: getStatusColor() }}>
                                {t('wms.orderReprint.processStatus.currentStatus')}{statusMessage}
                            </span>
                        </div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                            {processingStatus === 'idle' && <span style={{ color: '#999' }}>{t('wms.orderReprint.processStatus.normalWaiting')}</span>}
                            {processingStatus === 'generating' && <span style={{ color: '#999' }}>{t('wms.orderReprint.processStatus.pleaseWait')}</span>}
                            {processingStatus === 'printing' && <span style={{ color: '#999' }}>{t('wms.orderReprint.processStatus.sendingToPrinter')}</span>}
                            {(processingStatus === 'success' || processingStatus === 'confirmed') && <span style={{ color: '#999' }}>{t('wms.orderReprint.processStatus.readyForNext')}</span>}
                            {processingStatus === 'waitingConfirm' && <span style={{ color: '#722ed1' }}>{t('wms.orderReprint.status.scanToConfirm')}</span>}
                            {processingStatus === 'queryFailed' && (
                                <span style={{ color: '#fa8c16' }}>{t('wms.orderReprint.processStatus.queryFailedHint')}</span>
                            )}
                            {(processingStatus === 'notTemuUsps' || processingStatus === 'noRelabel') && (
                                <span style={{ color: '#fa8c16' }}>{t('wms.orderReprint.processStatus.checkAndRetry')}</span>
                            )}
                            {processingStatus === 'printFailed' && (
                                <span style={{ color: '#ff4d4f' }}>{t('wms.orderReprint.processStatus.printFailedHint')}</span>
                            )}
                        </div>
                    </div>

                    {/* 待确认 / 已确认 — 外部运单号展示 */}
                    {processingStatus === 'waitingConfirm' && pendingConfirmation && (
                        <div style={{ textAlign: 'center', marginBottom: 16, padding: '16px 20px', background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8 }}>
                            <div style={{ color: '#999', fontSize: 13, marginBottom: 4 }}>{t('wms.orderReprint.status.expectedTrackingNumber')}</div>
                            <div style={{ color: '#722ed1', fontSize: 24, fontWeight: 700, letterSpacing: 1 }}>{pendingConfirmation.externalTrackingNumber}</div>
                            {lastScannedCode && (
                                <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: 24, fontWeight: 700, letterSpacing: 1 }}>
                                    {t('wms.orderReprint.status.scannedCode')}{lastScannedCode}
                                </div>
                            )}
                            <div style={{ color: '#722ed1', fontSize: 16, fontWeight: 600, marginTop: 8 }}>{t('wms.orderReprint.status.pleaseScanReplacedLabel')}</div>
                        </div>
                    )}
                    {processingStatus === 'confirmed' && confirmedTrackingNumber && (
                        <div style={{ textAlign: 'center', marginBottom: 16, padding: '16px 20px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
                            <div style={{ color: '#52c41a', fontSize: 24, fontWeight: 700, letterSpacing: 1 }}>{confirmedTrackingNumber}</div>
                            <div style={{ color: '#999', fontSize: 13, marginTop: 6 }}>{t('wms.orderReprint.status.relabelConfirmed')}</div>
                        </div>
                    )}

                    {/* Latest Record */}
                    {latestRecord && (
                        <div
                            style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: 8,
                                padding: 16,
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                                {t('wms.orderReprint.processStatus.latestRecord')}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'center' }}>
                                <span style={{ color: '#999', whiteSpace: 'nowrap' }}>{t('wms.orderReprint.processStatus.trackingNumber')}</span>
                                <span style={{ fontWeight: 600, textAlign: 'right', overflowWrap: 'anywhere' }}>{latestRecord.trackingNumber}</span>

                                {latestRecord.recipient && (
                                    <>
                                        <span style={{ color: '#999', whiteSpace: 'nowrap' }}>{t('wms.orderReprint.processStatus.recipient')}</span>
                                        <span style={{ fontWeight: 600, textAlign: 'right', whiteSpace: 'pre-line' }}>{latestRecord.recipient}</span>
                                    </>
                                )}

                                <span style={{ color: '#999', whiteSpace: 'nowrap' }}>{t('wms.orderReprint.processStatus.status')}</span>
                                <div style={{ textAlign: 'right' }}>
                                    <Tag color={latestRecord.status === 'success' ? 'success' : latestRecord.status === 'failed' ? 'error' : 'processing'}>
                                        {latestRecord.status === 'success'
                                            ? t('wms.orderReprint.processStatus.printDone')
                                            : latestRecord.status === 'failed'
                                                ? t('wms.orderReprint.processStatus.printFailedShort')
                                                : t('wms.orderReprint.processStatus.printingShort')}
                                    </Tag>
                                </div>

                                <span style={{ color: '#999', whiteSpace: 'nowrap' }}>{t('wms.orderReprint.processStatus.time')}</span>
                                <span style={{ fontWeight: 600, textAlign: 'right' }}>{latestRecord.time}</span>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Footer Stats */}
            <div
                style={{
                    background: '#fff',
                    padding: '12px 24px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 48,
                    fontSize: 14,
                }}
            >
                <span>
                    {t('wms.orderReprint.stats.todayStats')}{t('wms.orderReprint.stats.processed')}{' '}
                    <strong style={{ color: '#1890ff', fontSize: 18 }}>{dailyStats.total}</strong>{' '}
                    {t('wms.orderReprint.stats.unit')}
                </span>
                <span>
                    {t('wms.orderReprint.stats.success')}{' '}
                    <strong style={{ color: '#52c41a', fontSize: 18 }}>{dailyStats.success}</strong>{' '}
                    {t('wms.orderReprint.stats.unit')}
                </span>
                <span>
                    {t('wms.orderReprint.stats.failed')}{' '}
                    <strong style={{ color: '#ff4d4f', fontSize: 18 }}>{dailyStats.failed}</strong>{' '}
                    {t('wms.orderReprint.stats.unit')}
                </span>
                <span>
                    {t('wms.orderReprint.stats.runningTime')}{' '}
                    <strong style={{ color: '#52c41a', fontSize: 18 }}>{runningTime}</strong>
                </span>
            </div>

            {/* Settings Drawer */}
            <Drawer
                title={t('wms.orderReprint.settingsDrawer.title')}
                open={settingsVisible}
                onClose={() => setSettingsVisible(false)}
                width={360}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            {t('wms.orderReprint.settingsDrawer.selectPrinter')}
                        </div>
                        <Select
                            value={printerName}
                            onChange={(val) => {
                                setPrinterName(val);
                                localStorage.setItem(PRINTER_STORAGE_KEY, val);
                            }}
                            style={{ width: '100%' }}
                            placeholder={t('wms.orderReprint.settingsDrawer.defaultPrinter')}
                            options={printerList.map(name => ({
                                value: name,
                                label: (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{name}</span>
                                        {name === defaultPrinterName && (
                                            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{t('wms.orderReprint.settingsDrawer.defaultPrinter')}</Tag>
                                        )}
                                    </div>
                                ),
                            }))}
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            {t('wms.orderReprint.settingsDrawer.copies')}
                        </div>
                        <InputNumber
                            min={1}
                            max={10}
                            value={copies}
                            onChange={(v) => v && setCopies(v)}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            {t('wms.orderReprint.printService.title')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Badge color={serviceReady ? 'green' : 'red'} />
                            <span>{serviceReady ? t('wms.orderReprint.printService.connected') : t('wms.orderReprint.printService.notRunning')}</span>
                        </div>
                        <Button onClick={detectService} loading={serviceChecking} style={{ marginBottom: 16 }}>
                            {t('wms.orderReprint.printService.retry')}
                        </Button>
                        <div style={{ background: '#f9f9f9', borderRadius: 6, padding: 12, fontSize: 13 }}>
                            <div style={{ fontWeight: 500, marginBottom: 8 }}>{t('wms.orderReprint.printService.installGuide')}</div>
                            {isMac ? (
                                <>
                                    <ol style={{ paddingLeft: 16, margin: 0, lineHeight: '24px', color: '#555' }}>
                                        <li>{t('wms.orderReprint.printService.macStep1')}</li>
                                        <li>{t('wms.orderReprint.printService.macStep2')}</li>
                                        <li>{t('wms.orderReprint.printService.macStep3')}</li>
                                    </ol>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            href="/print-service/mac-print-service-setup.zip"
                                            download="mac-print-service-setup.zip"
                                            type="primary"
                                        >
                                            {t('wms.orderReprint.printService.downloadMac')}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <ol style={{ paddingLeft: 16, margin: 0, lineHeight: '24px', color: '#555' }}>
                                        <li>{t('wms.orderReprint.printService.winStep1')}</li>
                                        <li>{t('wms.orderReprint.printService.winStep2')}</li>
                                        <li>{t('wms.orderReprint.printService.winStep3')}</li>
                                    </ol>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            href="/print-service/win-print-service-setup.zip"
                                            download="win-print-service-setup.zip"
                                            type="primary"
                                        >
                                            {t('wms.orderReprint.printService.downloadWin')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Drawer>

            {/* Log Drawer */}
            <Drawer
                title={t('wms.orderReprint.logDrawer.title')}
                open={logVisible}
                onClose={() => setLogVisible(false)}
                width={520}
            >
                {/* 来源筛选 */}
                <div style={{ marginBottom: 12 }}>
                    <Select
                        value={logSource}
                        onChange={(val) => {
                            setLogSource(val);
                            setLogPage(1);
                            if (currentWarehouse?.id) {
                                fetchLogs({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, accountId: accountIdForSource(val), source: val, page: 1, size: LOG_PAGE_SIZE } });
                                fetchDrawerStats({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, accountId: accountIdForSource(val), source: val } });
                            }
                        }}
                        style={{ width: 180 }}
                        options={[
                            { value: 1, label: t('wms.orderReprint.source.manual') },
                            { value: 2, label: t('wms.orderReprint.source.sorter') },
                        ]}
                    />
                </div>

                {/* 汇总统计 */}
                {drawerStatsData?.getOrderReprintDailyStats?.length ? ((() => {
                    const days = drawerStatsData.getOrderReprintDailyStats;
                    const total = days.reduce((s, d) => s + Number(d.total), 0);
                    const success = days.reduce((s, d) => s + Number(d.success), 0);
                    const failed = days.reduce((s, d) => s + Number(d.failed), 0);
                    const successRate = total > 0 ? (success / total * 100) : 0;
                    const totalMs = days.reduce((s, d) => s + Number(d.avgProcessingTimeMs) * Number(d.total), 0);
                    const avgMs = total > 0 ? totalMs / total : 0;
                    return (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, padding: '12px 16px', background: '#f9f9f9', borderRadius: 8 }}>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#1890ff' }}>{total}</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{t('wms.orderReprint.stats.processed')}</div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#52c41a' }}>{success}</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{t('wms.orderReprint.stats.success')}</div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#ff4d4f' }}>{failed}</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{t('wms.orderReprint.stats.failed')}</div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#52c41a' }}>{successRate.toFixed(1)}%</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{t('wms.orderReprint.logDrawer.successRate')}</div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#722ed1' }}>{(avgMs / 1000).toFixed(1)}s</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{t('wms.orderReprint.logDrawer.avgTime')}</div>
                            </div>
                        </div>
                    );
                })()) : null}

                {/* 日志列表 */}
                {logsLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><LoadingOutlined /></div>
                ) : !logsData?.getOrderReprintLogs?.content?.length ? (
                    <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                        {t('wms.orderReprint.logDrawer.noRecords')}
                    </div>
                ) : (
                    <>
                    <List
                        dataSource={logsData.getOrderReprintLogs.content}
                        renderItem={(record: OrderReprintLogItem) => (
                            <List.Item>
                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <Typography.Text strong style={{ fontSize: 13 }}>
                                                {record.neutralTrackingNumber}
                                            </Typography.Text>
                                            {record.trackingNumber && record.trackingNumber !== record.neutralTrackingNumber && (
                                                <div style={{ color: '#888', fontSize: 12 }}>→ {record.trackingNumber}</div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Tag color={record.source === 2 ? 'purple' : 'default'}>
                                                {record.source === 2 ? t('wms.orderReprint.source.sorter') : t('wms.orderReprint.source.manual')}
                                            </Tag>
                                            <span style={{ color: '#bbb', fontSize: 11 }}>{Number(record.processingTimeMs)}ms</span>
                                            <Tag color={record.status === 1 ? 'success' : 'error'}>
                                                {record.status === 1
                                                    ? t('wms.orderReprint.processStatus.printDone')
                                                    : t('wms.orderReprint.processStatus.printFailedShort')}
                                            </Tag>
                                        </div>
                                    </div>
                                    <div style={{ color: '#999', fontSize: 12, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{new Date(record.createdAt * 1000).toLocaleString(undefined, { hour12: false })}</span>
                                        {record.failureReason && (
                                            <span style={{ color: '#ff4d4f' }}>{record.failureReason}</span>
                                        )}
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                        <Pagination
                            current={logPage}
                            pageSize={LOG_PAGE_SIZE}
                            total={logsData.getOrderReprintLogs.totalElements}
                            showTotal={(total) => t('table.pagination.totalItems', { total })}
                            onChange={(page) => {
                                setLogPage(page);
                                if (currentWarehouse?.id) {
                                    fetchLogs({ variables: { hubId: currentHubId, warehouseId: currentWarehouseId || undefined, accountId: accountIdForSource(logSource), source: logSource, page, size: LOG_PAGE_SIZE } });
                                }
                            }}
                            size="small"
                        />
                    </div>
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default OrderReprintPage;
